/**
 * Iter XX.129 — Progressive Commitment Engine card.
 *
 * Tap-first onboarding surface. Renders as a full-viewport overlay
 * (mirrors PaidInstagramPersonaliseGate) so it never fights the chat
 * surface for real estate. Every tap:
 *   1. Immediately shows Gully's coach-voice reply for that tap.
 *   2. Then reveals the next question — or, on handoff, hands over to
 *      the free-form composer with a purposeful placeholder.
 *
 * Copy, tree, ordering, engagement budget and even the handoff message
 * are all config-driven server-side (question_engine_config.json).
 */
import { useEffect, useRef, useState } from "react";
import { pceStart, pceAnswer, pceSkip, markPceDone, RECOGNITION_LINE_1, RECOGNITION_LINE_2 } from "../lib/progressiveCommitmentEngine";
import { track } from "../lib/clarity";
import api from "../lib/api";

function _emit(event, payload, sessionId) {
  try { track(event, payload || {}); } catch { /* noop */ }
  try {
    if (sessionId) {
      api.post("/web/track", { session_id: sessionId, event, payload: payload || {} })
        .catch(() => {});
    }
  } catch { /* noop */ }
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId     Current chat session id.
 * @param {function} props.onHandoff     Called with { context } once the engine hands off.
 * @param {function} props.onSkip        Optional — user tapped skip / typed early.
 */
export default function QuestionEngineCard({ sessionId, onHandoff, onSkip }) {
  const [state, setState] = useState({
    status: "loading",
    question: null,
    replyStack: [],
    progress: { answered: 0, max_questions: 5 },
    handoff: null,
    error: null,
  });
  const questionShownAtRef = useRef(0);
  const runStartedRef = useRef(false);

  // Kick off on mount.
  useEffect(() => {
    if (!sessionId) return;
    if (runStartedRef.current) return;
    runStartedRef.current = true;
    (async () => {
      try {
        const data = await pceStart(sessionId);
        _emit("pce_started", { session_id: sessionId }, sessionId);
        if (data.status === "handoff") {
          markPceDone();
          _emit("pce_handoff", { questions_answered: 0, immediate: true }, sessionId);
          onHandoff?.({ context: null, handoff: data.handoff });
          return;
        }
        questionShownAtRef.current = performance.now();
        _emit("pce_question_shown", { question_id: data.question?.id }, sessionId);
        setState({
          status: "in_progress",
          question: data.question,
          replyStack: [],
          progress: data.progress || { answered: 0, max_questions: 5 },
          handoff: null,
          error: null,
        });
      } catch (e) {
        // Server refused (flag off, not eligible, etc.). Silently skip.
        onSkip?.({ reason: "start_failed", status: e.status });
      }
    })();
  }, [sessionId, onHandoff, onSkip]);

  async function _pick(answer) {
    if (state.status !== "in_progress" || !state.question) return;
    const latencyMs = Math.max(0, Math.round(performance.now() - questionShownAtRef.current));
    _emit("pce_answer_selected", {
      question_id: state.question.id,
      answer_id: answer.id,
      latency_ms: latencyMs,
    }, sessionId);
    // Optimistically transition to "advancing" so buttons disable.
    setState((s) => ({ ...s, status: "advancing" }));
    try {
      const data = await pceAnswer(sessionId, state.question.id, answer.id, latencyMs);
      const newReply = data.immediate_reply || "";
      const replyStack = [...state.replyStack, {
        question: state.question,
        answer,
        reply: newReply,
      }];
      if (data.status === "handoff") {
        setState({
          status: "handing_off",
          question: null,
          replyStack,
          progress: data.progress || state.progress,
          handoff: data.handoff,
          error: null,
        });
        markPceDone();
        _emit("pce_handoff", {
          questions_answered: replyStack.length,
        }, sessionId);
        // Let the last reply be visible for a beat before we hand off.
        setTimeout(() => {
          onHandoff?.({ context: null, handoff: data.handoff, replyStack });
        }, 900);
        return;
      }
      questionShownAtRef.current = performance.now();
      _emit("pce_question_shown", { question_id: data.question?.id }, sessionId);
      setState({
        status: "in_progress",
        question: data.question,
        replyStack,
        progress: data.progress || state.progress,
        handoff: null,
        error: null,
      });
    } catch (e) {
      setState((s) => ({ ...s, status: "in_progress", error: e.message || "network" }));
    }
  }

  function _skip() {
    _emit("pce_abandoned", {
      last_question_id: state.question?.id || null,
      answered: state.replyStack.length,
    }, sessionId);
    markPceDone();
    pceSkip(sessionId, "user_skip").catch(() => {});
    onSkip?.({ reason: "user_skip" });
  }

  if (state.status === "loading") {
    // Iter XX.133 — silent loading. When PCE was preloaded from the
    // ParentEntryGate, the first question is ALREADY in memory by
    // the time this mounts, so we simply hold a black frame instead
    // of showing "Gully is warming up…" copy. If the preload is
    // still in flight (rare cold-start), we render the same black
    // frame; users perceive "instant" whether it takes 5ms or 200ms.
    return (
      <div
        data-testid="pce-card"
        data-loading="1"
        className="fixed inset-0 z-40 bg-neutral-950"
      />
    );
  }

  const progressDots = [];
  const max = state.progress?.max_questions || 5;
  for (let i = 0; i < max; i += 1) {
    progressDots.push(
      <span
        key={i}
        className={`inline-block h-1.5 w-6 rounded-full transition-colors duration-300 ${
          i < (state.progress?.answered || 0)
            ? "bg-emerald-500"
            : "bg-neutral-800"
        }`}
      />,
    );
  }

  return (
    <div
      data-testid="pce-card"
      className="fixed inset-0 z-40 bg-neutral-950 overflow-y-auto flex flex-col"
    >
      <div className="w-full max-w-md mx-auto px-5 pt-8 pb-24 flex-1 flex flex-col">
        {/* Progress + skip */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-1.5" data-testid="pce-progress">
            {progressDots}
          </div>
          <button
            type="button"
            onClick={_skip}
            data-testid="pce-skip"
            className="text-xs text-neutral-500 hover:text-neutral-300 underline underline-offset-4"
          >
            Skip
          </button>
        </div>

        {/* Prior reply bubbles — coach voice, live continuity. */}
        <div className="space-y-3 mb-6" data-testid="pce-reply-stack">
          {state.replyStack.map((row, idx) => (
            <div key={`row-${idx}`} className="space-y-2">
              <div
                className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-emerald-900/40 border border-emerald-800/60 px-4 py-2.5 text-neutral-100 text-sm"
                data-testid={`pce-user-bubble-${idx}`}
              >
                {row.answer.label}
              </div>
              {row.reply && (
                <div
                  className="mr-auto max-w-[92%] rounded-2xl rounded-tl-sm bg-neutral-900 border border-neutral-800 px-4 py-3 text-neutral-100 text-[15px] leading-relaxed"
                  data-testid={`pce-reply-bubble-${idx}`}
                >
                  {row.reply}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Current question */}
        {state.question && (
          <div className="space-y-4" data-testid="pce-question">
            {/* Iter XX.133 — Warm recognition intro. Rendered ONCE
                above the very first question card (state.replyStack
                is still empty at that point), carrying the copy that
                used to live on the removed "identity" Continue screen
                in ParentEntryGate. No button, no interstitial —
                just a soft line so the flow feels like ONE
                conversation from gate → first tap. Hidden on all
                subsequent questions and after handoff. */}
            {state.replyStack.length === 0 && (
              <div
                className="mb-2"
                data-testid="pce-recognition-intro"
              >
                {(() => {
                  let l1 = ""; let l2 = "";
                  try {
                    l1 = sessionStorage.getItem("g_pce_intro_line_1") || "";
                    l2 = sessionStorage.getItem("g_pce_intro_line_2") || "";
                  } catch { /* noop */ }
                  // Iter XX.133 — fallback to the shared canonical
                  // constants when sessionStorage is empty (this is
                  // the fresh-anon eager-PCE path where the gate
                  // never ran to stash them).
                  if (!l1) l1 = RECOGNITION_LINE_1;
                  if (!l2) l2 = RECOGNITION_LINE_2;
                  if (!l1 && !l2) return null;
                  return (
                    <>
                      {l1 && (
                        <p className="text-neutral-100 text-lg leading-snug">
                          {l1}
                        </p>
                      )}
                      {l2 && (
                        <p className="mt-1 text-neutral-400 text-sm leading-snug">
                          {l2}
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            <div
              className="mr-auto max-w-[92%] rounded-2xl rounded-tl-sm bg-neutral-900 border border-neutral-800 px-4 py-3 text-neutral-100 text-[16px] leading-relaxed"
              data-testid="pce-question-prompt"
            >
              <div>{state.question.prompt}</div>
              {state.question.subprompt && (
                <div className="mt-1 text-neutral-500 text-sm">
                  {state.question.subprompt}
                </div>
              )}
            </div>
            <div className="space-y-2.5 pt-1">
              {(state.question.answers || []).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  disabled={state.status !== "in_progress"}
                  onClick={() => _pick(a)}
                  data-testid={`pce-answer-${a.id}`}
                  className="w-full text-left rounded-2xl border border-neutral-800 bg-neutral-900/70 px-4 py-3.5 text-neutral-100 text-[15px] hover:border-emerald-700 hover:bg-neutral-900 active:scale-[0.99] disabled:opacity-50 transition-all"
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Handing-off view: keep the last reply visible while the composer unlocks. */}
        {state.status === "handing_off" && state.handoff && (
          <div
            className="mt-4 mr-auto max-w-[92%] rounded-2xl rounded-tl-sm bg-neutral-900 border border-emerald-900/40 px-4 py-3 text-neutral-100 text-[15px] leading-relaxed"
            data-testid="pce-handoff-message"
          >
            {state.handoff.message}
          </div>
        )}

        {state.error && (
          <div
            className="mt-4 text-sm text-amber-400"
            data-testid="pce-error"
          >
            Something got in the way — try that again.
          </div>
        )}
      </div>
    </div>
  );
}
